import React from "react";

import { showErrorToast, showWarningToast } from "@shared/toasts/Toasts";

import { ScreenViewClient } from "../api/ScreenViewClient";
import { ConnectionStatus, ScreenViewClientConfig, StreamingMode } from "../types";

interface UseScreenViewResult {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    setCanvas: (canvas: HTMLCanvasElement | null) => void;
    isConnected: boolean;
    status: ConnectionStatus;
    error: string | null;
    warning: string | null;
    streamingMode: StreamingMode;
    currentRTT: number;
    sendMouseClick: (x: number, y: number, button: number, isDown: number) => void;
    sendMouseMove: (x: number, y: number) => void;
    sendMouseWheel: (deltaX: number, deltaY: number) => void;
    sendKey: (
        key: string,
        isDown: number,
        modifiers: {
            ctrl: number;
            alt: number;
            shift: number;
            meta: number;
        },
    ) => void;
    reconnect: () => void;
    disconnect: () => void;
    switchToH264: (e: React.MouseEvent<HTMLButtonElement>) => void;
    switchToScreenshots: (e: React.MouseEvent<HTMLButtonElement>) => void;
    switchToAdaptive: (e: React.MouseEvent<HTMLButtonElement>) => void;
    retryH264: () => void;
}

export function useScreenView(config: ScreenViewClientConfig): UseScreenViewResult {
    const [isConnected, setIsConnected] = React.useState(false);
    const [status, setStatus] = React.useState<ConnectionStatus>(ConnectionStatus.Disconnected);
    const [error, setError] = React.useState<string | null>(null);
    const [streamingMode, setStreamingMode] = React.useState<StreamingMode>(StreamingMode.Adaptive);
    const [currentRTT, setCurrentRTT] = React.useState(0);
    const [warning, setWarning] = React.useState<string | null>(null);

    const mirrorRef = React.useRef<ScreenViewClient | null>(null);
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

    const setCanvas = React.useCallback((canvas: HTMLCanvasElement | null) => {
        canvasRef.current = canvas;
        if (canvas && mirrorRef.current) {
            mirrorRef.current.setCanvas(canvas);
        }
    }, []);

    const sendMouseClick = React.useCallback((x: number, y: number, button: number, isDown: number) => {
        mirrorRef.current?.sendMouseClick(x, y, button, isDown);
    }, []);

    const sendMouseMove = React.useCallback((x: number, y: number) => {
        mirrorRef.current?.sendMouseMove(x, y);
    }, []);

    const sendMouseWheel = React.useCallback((deltaX: number, deltaY: number) => {
        mirrorRef.current?.sendMouseWheel(deltaX, deltaY);
    }, []);

    const sendKey = React.useCallback(
        (
            key: string,
            isDown: number,
            modifiers: {
                ctrl: number;
                alt: number;
                shift: number;
                meta: number;
            },
        ) => {
            mirrorRef.current?.sendKey(key, isDown, modifiers);
        },
        [],
    );

    const reconnect = React.useCallback(() => {
        if (mirrorRef.current) {
            mirrorRef.current.disconnect();
            setTimeout(() => {
                mirrorRef.current?.connect();
            }, 100);
        }
    }, []);

    const disconnect = React.useCallback(() => {
        mirrorRef.current?.disconnect();
    }, []);

    const switchToH264 = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        mirrorRef.current?.setStreamingMode(StreamingMode.H264);
        setStreamingMode(StreamingMode.H264);
    }, []);

    const switchToScreenshots = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        mirrorRef.current?.setStreamingMode(StreamingMode.Screenshot);
        setStreamingMode(StreamingMode.Screenshot);
    }, []);

    const switchToAdaptive = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        mirrorRef.current?.setStreamingMode(StreamingMode.Adaptive);
        setStreamingMode(StreamingMode.Adaptive);
    }, []);

    const retryH264 = React.useCallback(() => {
        mirrorRef.current?.retryH264();
    }, []);

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (mirrorRef.current) {
                setCurrentRTT(mirrorRef.current.getCurrentRTT());
                setStreamingMode(mirrorRef.current.getStreamingMode());
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        const mirror = new ScreenViewClient(config);
        mirrorRef.current = mirror;

        mirror.onConnectionEstablished(() => {
            setIsConnected(true);
            setError(null);
        });

        mirror.onConnectionLost(() => {
            setIsConnected(false);
        });

        mirror.onConnectionError((err) => {
            setError(err);
            setIsConnected(false);
        });

        mirror.onStatusChange((newStatus) => {
            setStatus(newStatus);
        });

        mirror.onScreenshotReceived(() => {});

        mirror.onError((message, metadata) => {
            setError(message);
            showErrorToast(message);
            // eslint-disable-next-line no-console
            console.error(message, metadata);
        });

        mirror.onWarning((message, metadata) => {
            setWarning(message);
            showWarningToast(message);
            // eslint-disable-next-line no-console
            console.warn(message, metadata);
        });

        if (canvasRef.current) {
            mirror.setCanvas(canvasRef.current);
        }

        mirror.connect();

        return () => {
            mirror.disconnect();
            mirrorRef.current = null;
        };
        // The session is the identity of the connection. `config` arrives as a fresh object on every
        // render of the caller, so depending on it would tear down and reopen the socket mid-stream.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.sessionId]);

    return {
        canvasRef,
        setCanvas,
        isConnected,
        status,
        error,
        warning,
        streamingMode,
        currentRTT,
        sendMouseClick,
        sendMouseMove,
        sendMouseWheel,
        sendKey,
        reconnect,
        disconnect,
        switchToH264,
        switchToScreenshots,
        switchToAdaptive,
        retryH264,
    };
}
